import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import { customPositionRowStyle } from '../../components/postionsWidget/BasketWisePostionContainer'
import { useCombineBasketAnalyzeAndLegOrders } from '../../hooks/useCombineBasketAnalyzeAndLegOrders'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectSimulatorDate } from '../../selectors/simulator'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import { useDispatch } from 'react-redux'
import { createStringDateFromStamp, createStringDateFromStampWithTime } from '../../util/tradePage'
import { setSimulatorOrderDataFunction, setSimulatorTimestamp } from '../../reducer/simulator.reducer'
import ExtraSmallTextField from '../../components/common/ExtraSmallTextField'
import { orderTypeToTransactionType, roundToNearestMultiple } from '../../util/tradepanel'
import { calculateSimulatorOrdersLiveData } from '../../reducer/options.reducer'
import useSelectPrimaryOrUnderlyingSecurity from '../../hooks/useSelectPrimaryOrUnderlyingSecurity'
import { ActionType, useUndoRedoContext } from '../../components/UndoRedoFunctonalities/UndoRedo.context'

const OrdersContainer = () => {
    const dispatch = useDispatch()
    const { push } = useUndoRedoContext()
    const { primaryOrUnderlyingToken: underlyingToken } = useSelectPrimaryOrUnderlyingSecurity()
    const { orders, ordersPrices } = useCombineBasketAnalyzeAndLegOrders()
    const timeStamp = useSelector(selectSimulatorDate)
    const inputPricesRefs = useRef<Array<HTMLInputElement>>([])
    const setInputPricesRef = (index: number) => (ref: HTMLInputElement) => {
        inputPricesRefs.current[index] = ref
    }

    const renderOrders = useMemo(() => {
        const obj = orders.map((order, i) => {
            return {
                order,
                orderPrice: ordersPrices[i]
            }
        }).filter((o) => ((o.order.timeStamp ?? 0) <= timeStamp) && o.order.traded).sort((a, b) => (a.order.timeStamp ?? 0) - (b.order.timeStamp ?? 0))
        obj.forEach((o,i)=>{
            if(inputPricesRefs.current?.[i]) {
                inputPricesRefs.current[i].value = o.orderPrice.price
            }
        })
        return obj
    }, [timeStamp, orders, ordersPrices])

    const [underlyingSecuritiesMap, setUnderlyingSecuritiesMap] = useState<Map<string, Awaited<ReturnType<typeof getSecurityAsync>>>>(new Map())
    const underlyingKeys = useMemo(() => [...new Set(renderOrders.map(o => getSecurityKey(o.order.security?.exchange ?? 0, o.order.security?.underlyingSecurityCode ?? '')).filter(Boolean))], [renderOrders])
    useEffect(() => {
        if (!underlyingKeys.length) { setUnderlyingSecuritiesMap(new Map()); return }
        Promise.all(underlyingKeys.map(k => getSecurityAsync(k))).then((secs) => {
            const m = new Map<string, Awaited<ReturnType<typeof getSecurityAsync>>>()
            underlyingKeys.forEach((k, i) => { if (secs[i]) m.set(k, secs[i]!) })
            setUnderlyingSecuritiesMap(m)
        })
    }, [underlyingKeys])

    const handleSetTimeStamp = useCallback((timeStamp: number) => {
        dispatch(setSimulatorTimestamp(timeStamp))
    }, [dispatch])

    const handlePriceBlur = useCallback((value: number, orderId: string, i: number) => {
        const newOrdersPrices = [...ordersPrices]
        const orderIndex = orders.findIndex((o) => o.uuid === orderId)
        if (orderIndex === -1) return
        newOrdersPrices[orderIndex] = {
            ...newOrdersPrices[orderIndex],
            price: (value).toFixed(2),
        }
        const price = roundToNearestMultiple(value, orderTypeToTransactionType(orders[orderIndex].type)).toFixed(2)
        if (inputPricesRefs.current[i]) {
            inputPricesRefs.current[i].value = price
        }
        push({ prev: { orders: orders, ordersPrices: ordersPrices }, new: { orders: orders, ordersPrices: newOrdersPrices }}, 'simulator orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
        setSimulatorOrderDataFunction({ orders, orderPrices: newOrdersPrices })
        calculateSimulatorOrdersLiveData(underlyingToken)
    }, [orders, ordersPrices, underlyingToken, push])

    return renderOrders.length === 0 ? null : (
        <>
            <TableContainer sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <Table stickyHeader sx={{
                    borderCollapse: 'collapse',
                    tableLayout: 'auto',
                    width: '100%',
                }}>
                    <TableHead>
                        <TableRow sx={customPositionRowStyle}>
                            <TableCell sx={{
                                padding: '0 10px',
                                paddingLeft: 0,
                                textAlign: 'right'
                            }}>
                                <Typography variant="caption"
                                    color={'textSecondary'}>TIME</Typography>
                            </TableCell>
                            <TableCell style={{ padding: '5px' }}>
                                <Typography variant="caption"
                                    color={'textSecondary'}>SYMBOL</Typography>
                            </TableCell>
                            <TableCell sx={{ padding: 0, textAlign: 'center' }}>
                                <Typography variant="caption" color={'textSecondary'}>B/S</Typography>
                            </TableCell>
                            <TableCell sx={{ padding: 0, textAlign: 'right' }}>
                                <Typography variant="caption"
                                    color={'textSecondary'}>QTY</Typography>
                            </TableCell>
                            <TableCell sx={{ padding: 0, textAlign: 'right' }}>
                                <Typography variant="caption"
                                    color={'textSecondary'}>PRICE</Typography>
                            </TableCell>
                            {/* <TableCell sx={{ padding: 0, textAlign: 'right', width: '8%' }}>
                                        <Typography variant="caption"
                                            color={'textSecondary'}>FILL</Typography>
                                    </TableCell> */}
                            {/* <TableCell
                                        sx={{ padding: 0, paddingRight: '2px', textAlign: 'right' }}>
                                        <Typography variant="caption"
                                            color={'textSecondary'}>AVG</Typography>
                                    </TableCell> */}
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ overflow: 'auto' }}>
                        {
                            renderOrders.map((order, i) => {
                                // const [time, ampm] = getTimeFromTimestamp((order.order.timeStamp) || 0)
                                const underlyingSecurity = underlyingSecuritiesMap.get(getSecurityKey(order.order.security?.exchange ?? 0, order.order.security?.underlyingSecurityCode ?? ''))
                                return (
                                    <TableRow sx={{
                                        borderBottom: theme => `1.5px solid ${theme.palette.background.default}`,
                                        height: '40px',
                                        position: 'relative',
                                        '&:hover .edit-icon-order': {
                                            display: 'table-cell !important'
                                        },
                                        '&:hover .edit-icon-replacement': {
                                            display: 'none !important'
                                        }
                                    }}>
                                        <TableCell sx={{
                                            padding: '5px 10px',
                                            paddingLeft: '2px',
                                            textAlign: 'right'
                                        }}>
                                            <Typography onClick={() => handleSetTimeStamp(order.order.timeStamp ?? 0)} style={{
                                                fontSize: '13px',
                                                textAlign: 'right',
                                                cursor: 'pointer',
                                            }}>{createStringDateFromStampWithTime(order.order.timeStamp ?? 0)}</Typography>
                                        </TableCell>
                                        <TableCell style={{
                                            padding: '5px 0',
                                            verticalAlign: 'middle'
                                        }}>
                                            <Typography style={{
                                                fontSize: '13px',
                                                padding: '0 5px'
                                            }}>
                                                {`${underlyingSecurity?.symbol ?? ''} ${createStringDateFromStamp(order.order.security?.expiry?.expiry ?? 0)} ${order.order.contractType === 'OPTION' ? order.order.strike / (order.order.security?.priceMultiplier ?? 100) : ''} ${order.order.contractType === 'OPTION' ? order.order.callput === 'call' ? 'CE' : 'PE' : ''}`}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    fontSize: '13px',
                                                    padding: '0 5px',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {/* {`${productToJSON(order.product)} ${order.orderType === OrderType.LIMIT_ORDER ? 'LIMIT' : order.orderType === OrderType.MARKET_ORDER ? 'MARKET' : orderTypeToJSON(order.orderType)}`}
                                                <FiberManualRecord style={{
                                                    fontSize: '6px',
                                                    marginRight: '3px',
                                                    marginLeft: '2px'
                                                }} />
                                                <span style={{ fontWeight: '600' }}
                                                    className={order.status === OrderStatus.EXECUTED ? 'green-text' : order.status === OrderStatus.OPEN ? 'red-text' : ''}>{orderStatusToJSON(order.status ?? 0)}</span> */}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            padding: '5px 0',
                                            textAlign: 'center',
                                            verticalAlign: 'middle'
                                        }}>
                                            <Typography fontSize={'13px'} fontWeight={'500'}
                                                className={order.order.type === 'buy' ? 'green-text' : 'red-text'}>{order.order.type === 'buy' ? 'B' : 'S'}</Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            padding: '5px 0',
                                            textAlign: 'right',
                                            verticalAlign: 'middle'
                                        }}>
                                            <Typography
                                                fontSize={'13px'}>{(order.order.quantity)*(order.order.security?.lotSize??0)}</Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            padding: '5px 0',
                                            textAlign: 'right',
                                            verticalAlign: 'middle'
                                        }}>
                                            <ExtraSmallTextField
                                                textFieldProps={{
                                                    defaultValue: order.orderPrice?.price,
                                                    inputRef: setInputPricesRef(i),
                                                    onBlur: (e) => handlePriceBlur(Number(e.target.value), order.order.uuid, i),
                                                    inputProps: {
                                                        sx: {
                                                            maxWidth: 54,
                                                            border: 'none',
                                                        }
                                                    },
                                                    type: 'number',
                                                    sx: {
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            border: 'none'
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            border: theme => `1px solid ${theme.palette.primary.main} !important`
                                                        },
                                                        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            border: theme => `1px solid ${theme.palette.primary.main} !important`
                                                        },
                                                    }
                                                }} hideEndIcon={true} />
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    )
}

export default OrdersContainer